import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

const tooltipVariants = cva(
  "z-50 overflow-hidden  rounded-md  px-3 py-1.5 text-sm  shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ",
  {
    variants: {
      color: {
        secondary: "border bg-popover text-popover-foreground",
        primary: "border border-primary bg-primary text-primary-foreground",
        warning: "border border-warning bg-warning text-warning-foreground",
        info: "border border-info bg-info text-info-foreground",
        success: "border border-success bg-success text-success-foreground",
        destructive:
          "border border-destructive bg-destructive text-destructive-foreground",
      },
    },
    defaultVariants: {
      color: "primary",
    },
  }
);

interface TolTipProps extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>, VariantProps<typeof tooltipVariants> {
  color?: 'primary' | 'secondary' | 'warning' | 'info' | 'success' | 'destructive'

}

// Context for managing tooltip state on mobile devices
interface TooltipContextValue {
  isMobile: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const TooltipContext = React.createContext<TooltipContextValue | null>(null);

const useTooltipContext = () => {
  const context = React.useContext(TooltipContext);
  return context;
};

const TooltipProvider = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Provider>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider>
>(
  ({ delayDuration = 0, ...props }, ref) => (
    <TooltipPrimitive.Provider

      {...props}
      delayDuration={delayDuration}
    />
  )
);
TooltipProvider.displayName = TooltipPrimitive.Provider.displayName;

// Wrapper for Tooltip with mobile device support
const Tooltip = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>
>((props, ref) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);

  const contextValue = React.useMemo(
    () => ({ isMobile, open, setOpen }),
    [isMobile, open]
  );

  // On mobile devices, control state manually
  if (isMobile) {
    return (
      <TooltipContext.Provider value={contextValue}>
        <TooltipPrimitive.Root
          {...props}
          open={open}
          onOpenChange={setOpen}
        />
      </TooltipContext.Provider>
    );
  }

  // On desktop, use standard behavior
  return (
    <TooltipContext.Provider value={contextValue}>
      <TooltipPrimitive.Root {...props} />
    </TooltipContext.Provider>
  );
});
Tooltip.displayName = "Tooltip";

// Wrapper for TooltipTrigger with mobile device support
const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ onClick, ...props }, ref) => {
  const context = useTooltipContext();
  const isMobile = context?.isMobile ?? false;

  // On mobile devices, add click handler to toggle state
  const handleClick = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (isMobile && context) {
      e.preventDefault();
      context.setOpen(!context.open);
    }
    onClick?.(e);
  }, [isMobile, context, onClick]);

  return (
    <TooltipPrimitive.Trigger
      ref={ref}
      {...props}
      onClick={handleClick}
      // On mobile devices, prevent opening on hover
      onPointerEnter={isMobile ? undefined : props.onPointerEnter}
      onPointerLeave={isMobile ? undefined : props.onPointerLeave}
    />
  );
});
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const TooltipArrow = TooltipPrimitive.Arrow;


const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TolTipProps
>(
  ({ className, sideOffset = 4, color, children, ...props }, ref) => {
    const context = useTooltipContext();
    const isMobile = context?.isMobile ?? false;

    // On mobile devices, add ability to close when clicking outside
    React.useEffect(() => {
      if (!isMobile || !context?.open) return;

      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        // Check that the click was outside the tooltip content
        if (!target.closest('[data-radix-tooltip-content]') && 
            !target.closest('[data-radix-tooltip-trigger]')) {
          context.setOpen(false);
        }
      };

      // Small delay to avoid immediate closing after opening
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside);
      };
    }, [isMobile, context]);

    return (
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(tooltipVariants({ color }), className, {})}
        data-radix-tooltip-content
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    );
  }
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipArrow,
};
